import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, ChevronRight, Clock, Calendar, Compass } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import chrisPhoto from "@/assets/chris-heidlebaugh-colorado-web-designer.png";
import { getCostArticleLinks, COST_GUIDES_HUB } from "@/lib/costArticleLinks";

export type LearnArticleFAQ = { q: string; a: string };
export type LearnTOCItem = { id: string; label: string };

export type LearnArticleLayoutProps = {
  /** Category badge, e.g. "GEO AI" */
  category: string;
  /** Article H1 */
  title: string;
  /** 1–2 sentence intro shown below the H1 */
  intro: string;
  /** "8 min read" etc. */
  readTime: string;
  /** ISO date e.g. "2026-04-29" */
  publishedDate: string;
  /** Optional ISO date — last meaningful content update. Falls back to publishedDate. */
  updatedDate?: string;
  /** Canonical path of this article, e.g. "/learn/what-is-geo-ai" */
  path: string;
  /** Table of contents links (anchor IDs must match section headings in body) */
  toc: LearnTOCItem[];
  /** Article body — section headings should use IDs matching the TOC */
  children: ReactNode;
  /** FAQs rendered at bottom + emitted as FAQPage JSON-LD */
  faqs: LearnArticleFAQ[];
  /**
   * Optional definition callout box rendered at the very top of the article body.
   * Highest-cited format for AI snippet extraction (ChatGPT, Perplexity, AI Overviews).
   * Provide a short term + 1–3 sentence plain-language definition.
   */
  definition?: { term: string; body: string };
};

const SITE_URL = "https://coloradowebimpressions.com";

function formatDate(iso: string): string {
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export function LearnArticleLayout({
  category,
  title,
  intro,
  readTime,
  publishedDate,
  updatedDate,
  path,
  toc,
  children,
  faqs,
  definition,
}: LearnArticleLayoutProps) {
  const url = `${SITE_URL}${path}`;
  const dateModified = updatedDate ?? publishedDate;
  const wasUpdated = updatedDate && updatedDate !== publishedDate;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: intro,
    author: {
      "@type": "Person",
      "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh`,
      name: "Chris Heidlebaugh",
      url: `${SITE_URL}/authors/chris-heidlebaugh`,
      jobTitle: "Owner, Colorado Web Impressions",
    },
    reviewedBy: {
      "@type": "Person",
      "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh`,
      name: "Chris Heidlebaugh",
    },
    publisher: {
      "@type": "Organization",
      name: "Colorado Web Impressions",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
      },
    },
    datePublished: publishedDate,
    dateModified: dateModified,
    image: `${SITE_URL}/logo.png`,
    inLanguage: "en-US",
    articleSection: category,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    isAccessibleForFree: true,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <SiteLayout>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <article className="bg-background">
        {/* Header */}
        <header className="bg-gradient-dark py-16 text-brand-cream sm:py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-1.5 text-xs font-medium text-brand-cream/85"
            >
              <Link to="/" className="hover:text-brand-gold">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/learn" className="hover:text-brand-gold">Learn</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-gold">{category}</span>
            </nav>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-brand-gold">
                {category}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-brand-cream/80">
                <Clock className="h-3 w-3" /> {readTime}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-brand-cream/80">
                <Calendar className="h-3 w-3" />
                {wasUpdated ? (
                  <>
                    <span className="text-brand-cream/80">Updated</span>{" "}
                    <time dateTime={dateModified}>{formatDate(dateModified)}</time>
                  </>
                ) : (
                  <time dateTime={publishedDate}>{formatDate(publishedDate)}</time>
                )}
              </span>
            </div>

            <h1 className="mt-5 text-3xl font-black leading-[1.1] text-white sm:text-4xl lg:text-5xl">
              {title}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-brand-cream/85">
              {intro}
            </p>

            {/* Author byline + E-E-A-T credentials. Visible signal that a real
                expert wrote and reviewed this content — Google's Helpful
                Content system + AI engines weight this heavily. */}
            <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
              <img
                src={chrisPhoto}
                alt="Chris Heidlebaugh, Owner of Colorado Web Impressions — Colorado Springs web designer and SEO expert"
                width={48}
                height={48}
                loading="eager"
                className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-brand-gold"
                data-geo-lat="38.9359976"
                data-geo-lng="-104.7653118"
                data-geo-city="Colorado Springs"
              />
              <div className="text-sm leading-tight text-brand-cream/85">
                <div className="font-semibold text-white">
                  Written &amp; reviewed by{" "}
                  <Link to="/authors/chris-heidlebaugh" className="text-brand-gold hover:underline">
                    Chris Heidlebaugh
                  </Link>
                </div>
                <div className="text-brand-cream/85">
                  Owner, Colorado Web Impressions · 19+ years in Colorado digital marketing
                </div>
                {wasUpdated && (
                  <div className="mt-1 text-xs text-brand-cream/80">
                    Originally published{" "}
                    <time dateTime={publishedDate}>{formatDate(publishedDate)}</time>
                    {" · Last reviewed "}
                    <time dateTime={dateModified}>{formatDate(dateModified)}</time>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="mx-auto max-w-6xl px-5 py-14 lg:px-8 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_18rem]">
            {/* Article column */}
            <div className="min-w-0">
              {/* Mobile TOC (collapsed) */}
              <details className="mb-8 rounded-xl border border-border bg-card p-4 shadow-card lg:hidden">
                <summary className="cursor-pointer text-sm font-bold text-foreground">
                  Table of contents
                </summary>
                <ul className="mt-3 space-y-2 text-sm">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-brand-gold-deep underline-offset-2 hover:underline"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>

              <div className="prose-article max-w-none text-base leading-relaxed text-foreground/90 [&_h2]:mt-10 [&_h2]:scroll-mt-28 [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-foreground [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground [&_p]:mt-4 [&_ul]:mt-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_strong]:font-bold [&_strong]:text-foreground [&_a]:font-semibold [&_a]:text-brand-gold-deep [&_a]:underline [&_a]:underline-offset-2">
                {definition && (
                  <aside
                    className="definition-box mb-8 rounded-xl border-l-4 border-brand-gold bg-brand-gold/5 p-5 sm:p-6"
                    aria-label={`Definition: ${definition.term}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
                      Definition
                    </div>
                    <p className="mt-2 text-base leading-relaxed text-foreground sm:text-lg">
                      <strong className="font-extrabold text-foreground">{definition.term}:</strong>{" "}
                      {definition.body}
                    </p>
                  </aside>
                )}
                {children}
              </div>

              {(() => {
                const related = getCostArticleLinks(path);
                if (!related) return null;
                return (
                  <section
                    aria-label="Keep exploring Colorado marketing costs"
                    className="mt-14 rounded-2xl border border-brand-gold/30 bg-card p-6 shadow-card sm:p-8"
                  >
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
                      <Compass className="h-4 w-4" /> Keep exploring
                    </div>
                    <h2 className="mt-2 text-2xl font-extrabold text-foreground sm:text-3xl">
                      Related services &amp; cost guides
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                      Take the next step — see the actual service this article
                      prices out, or browse every Colorado marketing cost
                      guide we've published.
                    </p>

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {related.map((link) => (
                        <a
                          key={link.to}
                          href={link.to}
                          className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                        >
                          <span>{link.label}</span>
                          <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                        </a>
                      ))}
                    </div>

                    <div className="mt-5 border-t border-border pt-5">
                      <Link
                        to={COST_GUIDES_HUB.to}
                        className="group inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
                      >
                        Browse the full Colorado cost-guide hub
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {COST_GUIDES_HUB.description}
                      </p>
                    </div>
                  </section>
                );
              })()}
              <section
                id="faq"
                aria-label="Frequently asked questions"
                className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8"
              >
                <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                  Frequently asked questions
                </h2>
                <div className="mt-6 divide-y divide-border">
                  {faqs.map((f) => (
                    <details key={f.q} className="group py-4">
                      <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-bold text-foreground">
                        <span>{f.q}</span>
                        <span className="mt-1 text-brand-gold-deep transition-transform group-open:rotate-45">
                          +
                        </span>
                      </summary>
                      <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                        {f.a}
                      </p>
                    </details>
                  ))}
                </div>
              </section>

              {/* End-of-article CTA */}
              <section className="mt-12 rounded-2xl border border-brand-gold/30 bg-gradient-to-br from-brand-gold/10 via-brand-gold/5 to-transparent p-7 sm:p-9">
                <h3 className="text-2xl font-extrabold text-foreground">
                  Want us to do this for your Colorado business?
                </h3>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">
                  Owner-led strategy from our Colorado Springs HQ. No
                  contracts, no outsourcing.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/contact"
                    className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                  >
                    Get a Free Audit
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="tel:+17194193935"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-3.5 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50"
                   aria-label="Call (719) 419-3935">
                    <Phone className="h-4 w-4" /> (719) 419-3935
                  </a>
                </div>
              </section>

              {/* Author bio box */}
              <aside
                aria-label="About the author"
                className="mt-12 rounded-2xl border border-border bg-card p-6 shadow-card sm:p-7"
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <img
                    src={chrisPhoto}
                    alt="Chris Heidlebaugh — owner of Colorado Web Impressions, Colorado Springs web designer"
                    width={96}
                    height={96}
                    loading="lazy"
                    className="h-20 w-20 shrink-0 rounded-full object-cover ring-2 ring-brand-gold shadow-gold"
                    data-geo-lat="38.9359976"
                    data-geo-lng="-104.7653118"
                    data-geo-city="Colorado Springs"
                  />
                  <div className="min-w-0">
                    <div className="text-lg font-extrabold text-foreground">
                      Chris Heidlebaugh
                    </div>
                    <div className="text-sm font-semibold text-brand-gold-deep">
                      Owner, Colorado Web Impressions
                    </div>
                    <address className="mt-2 not-italic text-sm text-muted-foreground">
                      3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920
                    </address>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/85 sm:text-base">
                  Chris Heidlebaugh has been running digital marketing
                  campaigns for Colorado businesses since 2006. He's the
                  founder and owner of Colorado Web Impressions and works
                  directly on every client account — no middlemen, no
                  outsourcing.
                </p>
              </aside>
            </div>

            {/* Sticky desktop TOC */}
            <aside
              aria-label="Table of contents"
              className="hidden lg:sticky lg:top-28 lg:block lg:self-start"
            >
              <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  In this article
                </div>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="block text-foreground/80 transition-smooth hover:text-brand-gold-deep"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a
                      href="#faq"
                      className="block text-foreground/80 transition-smooth hover:text-brand-gold-deep"
                    >
                      FAQs
                    </a>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}

/** Build head() metadata for any /learn/* article. */
export function learnArticleHead(opts: {
  title: string;
  description: string;
  path: string;
  category?: string;
}) {
  const canonical = `https://coloradowebimpressions.com${opts.path}`;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${canonical}#breadcrumbs`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
      { "@type": "ListItem", position: 2, name: "Learn", item: "https://coloradowebimpressions.com/learn" },
      ...(opts.category
        ? [{ "@type": "ListItem", position: 3, name: opts.category, item: canonical }]
        : [{ "@type": "ListItem", position: 3, name: opts.title, item: canonical }]),
    ],
  };
  return {
    meta: [
      { title: opts.title },
      { name: "description", content: opts.description },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: opts.description },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: opts.title },
      { name: "twitter:description", content: opts.description },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema),
      },
    ],
  };
}
